import React, { useState } from "react";
import {
    Table,
    Card,
    Alert,
    Placeholder,
    Badge,
} from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { useReportTags } from "@features/report/tag/hooks/useReportTags";
import NoResult from "@core/components/NoResult";
import TableActionButton from "@core/components/TableActionButton";
import TableActions from "@core/components/TableActions";
import EntityCard from "@core/components/EntityCard";
import { FiEdit2 } from "react-icons/fi";
import { subtleBadgeProps } from "@core/utils/subtleBadge";
import { getReportTagStatusLabel } from "@core/reports/utils/reportWorkflow";
import { ReportTagTableProps } from "@features/report/tag/types/reportTagTableProps";
import PaginationBar from "@core/components/PaginationBar";
import TablePaginationFooter, { getPaginationRange } from "@core/components/TablePaginationFooter";
import UpdateTagModal from "@features/report/tag/components/UpdateTagModal";
import RequestReopenModal from "@features/report/tag/components/RequestReopenModal";
import ApproveOrDenyReopenModal, { ReopenActionMode } from "@features/report/tag/components/ApproveOrDenyReopenModal";
import { getAvailableTagActions } from "@features/report/tag/utils/reportTagActions";
import { SearchBar, FilterField } from "@core/components/SearchBar";
import { useDebounce } from "@core/hooks/useDebounce";
import { useSearchUrlSync } from "@core/hooks/useSearchUrlSync";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { formatDateOnly, parseDateOnly } from "@core/utils/formatDateOnly";
import { formatReportTagDeadline } from "@features/report/tag/utils/reportTagDeadline";

const REPORT_TAG_FILTER_FIELDS: FilterField[] = [
    { name: 'reportTagName',   label: 'Tag Name',    type: 'text',   placeholder: 'Filter by tag name',        colWidth: 4 },
    { name: 'description',     label: 'Description', type: 'text',   placeholder: 'Filter by description',     colWidth: 4 },
    { name: 'reportTagStatus', label: 'Status',      type: 'select', placeholder: 'All statuses',              colWidth: 4,
      options: [
        { value: '',              label: 'All statuses'   },
        { value: 'OPEN',          label: 'Open'           },
        { value: 'CLOSED',        label: 'Closed'         },
        { value: 'DISABLED',      label: 'Disabled'       },
        { value: 'PENDING_REOPEN',label: 'Pending Reopen' },
      ],
    },
];

/** Map a status name to a Bootstrap Badge variant */
const getStatusVariant = (name?: string) => {
    const n = (name ?? "").toLowerCase();
    if (n.includes("open")) return "success";
    if (n.includes("closed") || n.includes("disabled")) return "secondary";
    if (n.includes("pending")) return "info";
    return "info";
};

const daysBetween = (since?: string | null, until?: string | null) => {
    const start = parseDateOnly(since);
    const end = parseDateOnly(until);
    if (!start || !end) return null;

    const diff = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));

    if (!Number.isFinite(diff)) return null;
    // inclusive ranges usually feel better for reporting periods
    return diff >= 0 ? diff + 1 : null;
};

const getPeriodParts = (since?: string | null, until?: string | null) => {
    const s = formatDateOnly(since);
    const u = formatDateOnly(until);
    const dur = daysBetween(since, until);

    return {
        sinceLabel: s ?? "—",
        untilLabel: u ?? "—",
        durationLabel: dur ? `${dur} day${dur === 1 ? "" : "s"}` : null,
    };
};

const ReportTagTable: React.FC<ReportTagTableProps> = ({
    nationId,
    showPagination = false,
    refreshSignal,
}) => {
    const [searchParams] = useSearchParams();
    const scopeId = useScopeId();
    const resolvedNationId = nationId ?? scopeId ?? 0;

    const [page, setPage] = useState(() => Number(searchParams.get('page')) || 1);
    const [searchQuery, setSearchQuery]   = useState(() => searchParams.get('q') ?? '');
    const [activeFilters, setActiveFilters] = useState<Record<string, any>>(() => {
        const f: Record<string, any> = {};
        REPORT_TAG_FILTER_FIELDS.forEach(({ name }) => {
            const v = searchParams.get(name);
            if (v) f[name] = v;
        });
        return f;
    });
    const debouncedQuery = useDebounce(searchQuery, 300);

    useSearchUrlSync({ query: debouncedQuery, filters: activeFilters, page });

    const [selectedTag, setSelectedTag] = useState<any | null>(null);
    const [show, setShowUpdateModal] = useState(false);

    // Reopen modals state
    const [reopenTag, setReopenTag] = useState<any | null>(null);
    const [showRequestReopen, setShowRequestReopen] = useState(false);
    const [showApproveOrDeny, setShowApproveOrDeny] = useState(false);
    const [approveOrDenyMode, setApproveOrDenyMode] = useState<ReopenActionMode>("approve");

    const activeFilterCount = Object.values(activeFilters).filter((v) => v !== '' && v != null).length;

    const appliedFilters = Object.fromEntries(Object.entries(activeFilters).filter(([, v]) => v !== '' && v != null));
    const hasSearch = !!(debouncedQuery || Object.keys(appliedFilters).length);

    // Fetch report tags using custom hook
    const { current_page, data, from, last_page, per_page, total, loading, reload } = useReportTags({
        nationId: resolvedNationId,
        page,
        ...(!hasSearch ? { all: true } : {}),
        freetext: debouncedQuery || undefined,
        ...appliedFilters,
    });

    // Reload when a sibling create succeeds (skip the initial mount).
    const didMountRef = React.useRef(false);
    React.useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        void reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshSignal]);

    // Calculate total pages
    const totalPages = Math.max(1, Math.ceil(total / (per_page ?? 20)));
    const pageNumber = current_page ?? page;
    const { start, end } = getPaginationRange({
        from,
        page: pageNumber,
        perPage: per_page,
        total,
        currentCount: data.length,
    });


    // guard
    if (!Number.isFinite(resolvedNationId) || resolvedNationId <= 0) {
        return (
            <Alert variant="danger" className="my-4 shadow-sm" data-testid="invalid-nation-id-alert">
                Error: Missing required parameter. <strong>scopeRefId</strong>.
            </Alert>
        );
    }

    // Status badge + action set shared by the desktop row and the mobile card.
    const renderTagStatus = (statusName?: string) => (
        <Badge {...subtleBadgeProps(getStatusVariant(statusName))}>
            {statusName ? getReportTagStatusLabel(statusName) : "—"}
        </Badge>
    );

    const renderTagPeriod = (t: (typeof data)[number]) => {
        const p = getPeriodParts(t.since, t.until);
        const deadlineLabel = formatReportTagDeadline(t.deadline) ?? "—";
        return `${p.sinceLabel} → ${p.untilLabel}${p.durationLabel ? ` · ${p.durationLabel}` : ""} · Deadline: ${deadlineLabel}`;
    };

    const renderTagActions = (t: (typeof data)[number]) => {
        const actions = getAvailableTagActions(t);
        return (
            <TableActions aria-label={`Actions for tag ${t.name}`}>
                {actions.showEdit && (
                    <TableActionButton
                        icon={<FiEdit2 aria-hidden="true" />}
                        label="Edit"
                        primary
                        onClick={() => {
                            setSelectedTag(t);
                            setShowUpdateModal(true);
                        }}
                        data-testid={`edit-tag-button-${t.report_tag_id}`}
                    />
                )}
                {actions.showRequestReopen && (
                    <TableActionButton
                        icon="arrow-repeat"
                        label="Request Reopen"
                        primary
                        variant="outline-primary"
                        onClick={() => {
                            setReopenTag(t);
                            setShowRequestReopen(true);
                        }}
                        data-testid={`request-reopen-button-${t.report_tag_id}`}
                    />
                )}
                {actions.showApprove && (
                    <TableActionButton
                        icon="check-lg"
                        label="Approve"
                        primary
                        variant="outline-success"
                        onClick={() => {
                            setReopenTag(t);
                            setApproveOrDenyMode("approve");
                            setShowApproveOrDeny(true);
                        }}
                        data-testid={`approve-reopen-button-${t.report_tag_id}`}
                    />
                )}
                {actions.showDeny && (
                    <TableActionButton
                        icon="x-lg"
                        label="Deny"
                        variant="outline-danger"
                        onClick={() => {
                            setReopenTag(t);
                            setApproveOrDenyMode("deny");
                            setShowApproveOrDeny(true);
                        }}
                        data-testid={`deny-reopen-button-${t.report_tag_id}`}
                    />
                )}
            </TableActions>
        );
    };

    return (
        <div>
            <div className="ntcog-list-search-section">
                <SearchBar
                    placeholder="Search report tags…"
                    filterFields={REPORT_TAG_FILTER_FIELDS}
                    initialQuery={searchQuery}
                    activeFilterCount={activeFilterCount}
                    loading={loading}
                    onQueryChange={(q) => { setSearchQuery(q); setPage(1); }}
                    onFiltersApply={(f) => { setActiveFilters(f); setPage(1); }}
                    onClear={() => { setSearchQuery(''); setActiveFilters({}); setPage(1); }}
                    data-testid="report-tag-search-bar"
                />
            </div>
            <Card className="shadow-sm ntcog-card ntcog-list-table-card" data-testid="report-tag-table-card">
                <Card.Body className="p-0">
                    <div className="px-3 pb-3 d-none d-md-block">
                        <Table responsive
                            hover
                            size="sm"
                            className="mb-0 align-middle table-striped ntcog-table" data-testid="report-tag-table"
                        >
                            <thead className="table-light">
                                <tr className="small text-muted">
                                    <th>#</th>
                                    <th>Name</th>
                                    <th>Description</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                    <th className="text-end" style={{ width: 180 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody className="table-group-divider">
                                {loading && Array.from({ length: 6 }).map((_, i) => (
                                    <tr key={`ph-${i}`} data-testid={`skeleton-row-${i}`}>
                                        <td className="text-muted"><Placeholder xs={6} /></td>
                                        <td className="text-muted"><Placeholder xs={6} /></td>
                                        <td className="text-muted"><Placeholder xs={8} /></td>
                                        <td className="text-muted"><Placeholder xs={4} /></td>
                                        <td className="text-muted"><Placeholder xs={6} /></td>
                                        <td className="text-end"><Placeholder xs={3} /></td>
                                    </tr>
                                ))}
                                {!loading && data.length === 0 && (
                                    <tr>
                                        <td colSpan={6} className="py-5">
                                            <NoResult line_1="No Tags found." line_2="Try adjusting filters or create a new Report Tag." />
                                        </td>
                                    </tr>
                                )}
                                {!loading && data.map((t: typeof data[number], i: number) => {
                                    const rowNumber = (from != null) ? from + i : i + 1;
                                    const statusName = t.report_tag_status_name;
                                    const p = getPeriodParts(t.since, t.until);
                                    const deadlineLabel = formatReportTagDeadline(t.deadline) ?? "—";
                                    return (
                                        <tr key={t.report_tag_id}>
                                            <td>
                                                {rowNumber}
                                            </td>
                                            <td>{t.name}</td>
                                            <td>{t.description}</td>
                                            <td>{renderTagStatus(statusName)}</td>
                                            <td style={{ minWidth: 220 }}>
                                                <div className="d-flex flex-column">
                                                    <div className="d-flex align-items-center gap-2">
                                                        <span className="fw-semibold">{p.sinceLabel}</span>
                                                        <span className="text-muted">→</span>
                                                        <span className="fw-semibold">{p.untilLabel}</span>
                                                    </div>

                                                    <div className="text-muted small">
                                                        {p.durationLabel ? `Period: ${p.durationLabel}` : "Period not fully set"}
                                                    </div>
                                                    <div className="text-muted small">
                                                        Deadline: {deadlineLabel}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-end">
                                                {renderTagActions(t)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>

                    {/* Mobile (< md): stacked cards */}
                    <div className="ntcog-card-list d-md-none" data-testid="report-tag-cards">
                        {loading && Array.from({ length: 6 }).map((_, i) => (
                            <div key={`ph-card-${i}`} className="ntcog-card-list__item">
                                <EntityCard
                                    testId={`tag-card-skeleton-${i}`}
                                    title={<Placeholder as="span" animation="glow"><Placeholder xs={6} /></Placeholder>}
                                    description={<Placeholder as="span" animation="glow"><Placeholder xs={8} /></Placeholder>}
                                />
                            </div>
                        ))}
                        {!loading && data.length === 0 && (
                            <NoResult line_1="No Tags found." line_2="Try adjusting filters or create a new Report Tag." />
                        )}
                        {!loading && data.map((t: typeof data[number]) => (
                            <div key={t.report_tag_id} className="ntcog-card-list__item">
                                <EntityCard
                                    testId={`tag-card-${t.report_tag_id}`}
                                    ariaLabel={`Report tag ${t.name}`}
                                    title={<span className="fw-semibold">{t.name}</span>}
                                    status={renderTagStatus(t.report_tag_status_name)}
                                    actions={renderTagActions(t)}
                                    description={t.description || undefined}
                                    details={[{ label: "Period", value: renderTagPeriod(t) }]}
                                />
                            </div>
                        ))}
                    </div>
                </Card.Body>
            </Card>

            {showPagination && (
                <TablePaginationFooter
                    className="mt-3"
                    testId="pagination-bar"
                    info={`Showing ${start} to ${end} of ${total} report tags`}
                >
                    <PaginationBar
                        page={pageNumber}
                        totalPages={last_page ?? totalPages}
                        onPage={(p) => setPage(p)}
                    />
                </TablePaginationFooter>
            )}

            <UpdateTagModal
                show={show}
                tag={selectedTag}
                onHide={() => setShowUpdateModal(false)}
                onUpdated={() => { reload(); }}
            />

            <RequestReopenModal
                show={showRequestReopen}
                tag={reopenTag}
                nationId={resolvedNationId}
                onHide={() => setShowRequestReopen(false)}
                onRequested={() => { reload(); }}
            />

            <ApproveOrDenyReopenModal
                show={showApproveOrDeny}
                tag={reopenTag}
                nationId={resolvedNationId}
                mode={approveOrDenyMode}
                onHide={() => setShowApproveOrDeny(false)}
                onCompleted={() => { reload(); }}
            />
        </div>
    );
};

export default ReportTagTable;
